import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, Scatter
} from 'recharts';
import { 
  LayoutDashboard, TrendingUp, TrendingDown, Users, DollarSign,
  Activity, Heart, Shield, AlertTriangle, RefreshCw, Loader2,
  Brain, Sparkles, Target, Zap, Clock, Calendar, FileText,
  CheckCircle2, XCircle, ArrowUpRight, ArrowDownRight, Minus,
  Building, HeartPulse, Award, PieChart as PieChartIcon,
  BarChart3, Gauge, Eye, Lightbulb, Bell, ChevronRight,
  Download, Filter, Settings2, Star, Flame, AlertCircle
} from 'lucide-react';
import { 
  PageHeader, MetricCard, ModernCard, SectionHeader, StatusBadge,
  modernTabsClass, modernTabTriggerClass, GradientProgress, RiskGauge
} from '@/components/ModernUI';
import { InsightsExport } from '@/components/InsightsExport';
import { DataHistoryPanel } from '@/components/DataHistory';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';
import { ReportTimeframeSelector } from '@/components/ReportTimeframeSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// COO-Critical KPIs grouped by domain
const KPI_DOMAINS = {
  financial: {
    name: 'Financial Health',
    icon: DollarSign,
    gradient: 'from-emerald-500 to-teal-600',
    kpis: [
      { id: 'revenue', name: 'Weekly Revenue', target: '$200K', unit: 'currency', critical: true },
      { id: 'net_margin', name: 'Net Margin', target: '12%', unit: 'percent', critical: true },
      { id: 'cost_per_episode', name: 'Cost per Episode', target: '<$25K', unit: 'currency', critical: false },
      { id: 'adr_rate', name: 'ADR Rate', target: '<5%', unit: 'percent', critical: false },
      { id: 'bad_debt', name: 'Bad Debt %', target: '<3%', unit: 'percent', critical: false }
    ]
  },
  clinical: {
    name: 'Clinical Quality',
    icon: Heart,
    gradient: 'from-rose-500 to-pink-600',
    kpis: [
      { id: 'pain_control', name: 'Pain Control (48h)', target: '≥90%', unit: 'percent', critical: true },
      { id: 'hospitalization', name: 'Hospitalization Rate', target: '<5%', unit: 'percent', critical: true },
      { id: 'live_discharge', name: 'Live Discharge Rate', target: '<3%', unit: 'percent', critical: true },
      { id: 'symptom_management', name: 'Symptom Management', target: '≥95%', unit: 'percent', critical: false },
      { id: 'missed_visits', name: 'Missed Visits', target: '<5%', unit: 'percent', critical: false }
    ]
  },
  workforce: {
    name: 'Workforce & Capacity',
    icon: Users,
    gradient: 'from-blue-500 to-indigo-600',
    kpis: [
      { id: 'staff_utilization', name: 'Staff Utilization', target: '75-85%', unit: 'percent', critical: true },
      { id: 'burnout_risk', name: 'Burnout Risk (Critical)', target: '0', unit: 'count', critical: true },
      { id: 'prn_utilization', name: 'PRN Utilization', target: '<12%', unit: 'percent', critical: false },
      { id: 'turnover_rate', name: 'Staff Turnover', target: '<20%', unit: 'percent', critical: false },
      { id: 'hiring_gap', name: 'FTE Hiring Gap', target: '0', unit: 'count', critical: false }
    ]
  },
  compliance: {
    name: 'Compliance & Audit',
    icon: Shield,
    gradient: 'from-violet-500 to-purple-600',
    kpis: [
      { id: 'overall_compliance', name: 'Overall Compliance', target: '≥95%', unit: 'percent', critical: true },
      { id: 'audit_readiness', name: 'Audit Readiness', target: '≥85%', unit: 'percent', critical: true },
      { id: 'cti_compliance', name: 'CTI Compliance', target: '100%', unit: 'percent', critical: true },
      { id: 'f2f_compliance', name: 'F2F Compliance', target: '100%', unit: 'percent', critical: true },
      { id: 'chap_score', name: 'CHAP Readiness', target: '≥90%', unit: 'percent', critical: false }
    ]
  },
  census: {
    name: 'Census & Operations',
    icon: Building,
    gradient: 'from-amber-500 to-orange-600',
    kpis: [
      { id: 'adc', name: 'Average Daily Census', target: '50+', unit: 'number', critical: true },
      { id: 'admissions', name: 'Weekly Admissions', target: '≥10', unit: 'count', critical: false },
      { id: 'discharges', name: 'Weekly Discharges', target: 'Balanced', unit: 'count', critical: false },
      { id: 'alos', name: 'Avg Length of Stay', target: '60-90 days', unit: 'days', critical: false },
      { id: 'referral_conversion', name: 'Referral Conversion', target: '≥70%', unit: 'percent', critical: false }
    ]
  },
  quality: {
    name: 'Patient Experience',
    icon: Star,
    gradient: 'from-pink-500 to-fuchsia-600',
    kpis: [
      { id: 'cahps_overall', name: 'CAHPS Overall', target: '≥90%', unit: 'percent', critical: true },
      { id: 'complaint_rate', name: 'Complaints/100 pts', target: '<5', unit: 'count', critical: false },
      { id: 'resolution_time', name: 'Resolution Time', target: '<5 days', unit: 'days', critical: false },
      { id: 'family_satisfaction', name: 'Family Satisfaction', target: '≥85%', unit: 'percent', critical: false }
    ]
  }
};

// KPI Card Component
const KPICard = ({ kpi, value, status, trend, trendValue, onClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500';
      case 'good': return 'bg-emerald-400';
      case 'warning': return 'bg-amber-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  const getTrendIcon = () => {
    if (trend === 'up') return <ArrowUpRight className="h-3 w-3" />;
    if (trend === 'down') return <ArrowDownRight className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all hover:-translate-y-0.5 ${kpi.critical ? 'ring-1 ring-inset ring-amber-200 dark:ring-amber-800' : ''}`}
      onClick={onClick}
      data-testid={`kpi-card-${kpi.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{kpi.name}</p>
            {kpi.critical && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 mt-1 border-amber-400 text-amber-600">Critical</Badge>
            )}
          </div>
          <div className={`h-2 w-2 rounded-full ${getStatusColor(status)}`} />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">Target: {kpi.target}</span>
          {trendValue && (
            <div className={`flex items-center gap-1 text-xs ${
              trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'
            }`}>
              {getTrendIcon()}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Alert Item Component
const AlertItem = ({ alert, onAction }) => {
  const severityConfig = {
    critical: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-l-red-500', icon: AlertTriangle, color: 'text-red-600' },
    high: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-l-amber-500', icon: AlertCircle, color: 'text-amber-600' },
    medium: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-l-yellow-500', icon: Bell, color: 'text-yellow-600' },
    low: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-l-blue-500', icon: Lightbulb, color: 'text-blue-600' }
  };

  const config = severityConfig[alert.severity] || severityConfig.medium;
  const Icon = config.icon;

  return (
    <div className={`p-4 rounded-lg border-l-4 ${config.border} ${config.bg}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${config.color} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className={`font-semibold text-sm ${config.color}`}>{alert.title}</h4>
            <Badge variant="outline" className="text-[10px]">{alert.domain}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
          {alert.recommendation && (
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 italic">
              Recommendation: {alert.recommendation}
            </p>
          )}
        </div>
        {alert.action && (
          <Button size="sm" variant="ghost" className="shrink-0" onClick={() => onAction(alert)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const typeConfig = {
    prediction: { icon: Brain, gradient: 'from-violet-500 to-purple-600' },
    trend: { icon: TrendingUp, gradient: 'from-blue-500 to-indigo-600' },
    recommendation: { icon: Lightbulb, gradient: 'from-amber-500 to-orange-600' },
    risk: { icon: AlertTriangle, gradient: 'from-red-500 to-rose-600' },
    opportunity: { icon: Star, gradient: 'from-emerald-500 to-teal-600' }
  };

  const config = typeConfig[insight.type] || typeConfig.trend;
  const Icon = config.icon;

  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${config.gradient}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-sm">{insight.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
            {insight.impact && (
              <Badge variant="secondary" className="mt-2 text-xs">
                Impact: {insight.impact}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ExecutiveCommandCenter() {
  const { token, user } = useAuth();
  const { config, getRNCMCaseloadTarget, getTargetADC, getWorkforceTarget, getClinicalTarget, getFinancialTarget } = useConfig();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  
  // Data states
  const [kpiData, setKpiData] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [insights, setInsights] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [weekOverWeek, setWeekOverWeek] = useState({ highlights: [], comparisons: {} });
  
  // Get configurable targets from context
  const configTargets = useMemo(() => ({
    rncmCaseload: getRNCMCaseloadTarget(),
    targetADC: getTargetADC(),
    staffUtilization: getWorkforceTarget('staff_utilization_target', 0.85),
    painControlTarget: getClinicalTarget('pain_control_target', 0.95),
    hospitalizationTarget: getClinicalTarget('hospitalization_target', 0.03),
    cahpsTarget: getClinicalTarget('cahps_target', 93),
    revenueTarget: getFinancialTarget('monthly_revenue_target', 200000),
    netMarginTarget: getFinancialTarget('net_margin_target', 0.12),
  }), [getRNCMCaseloadTarget, getTargetADC, getWorkforceTarget, getClinicalTarget, getFinancialTarget]);

  // Fetch week-over-week data
  const fetchWeekOverWeek = async () => {
    try {
      const res = await fetch(`${API_URL}/api/command-center/week-over-week`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWeekOverWeek(data);
      }
    } catch (error) {
      console.error('Error fetching week-over-week data:', error);
    }
  };

  // Fetch all command center data
  const fetchCommandCenterData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/command-center/dashboard?time_range=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setKpiData(data.kpis || {});
        setAlerts(data.alerts || []);
        setInsights(data.insights || []);
        setPredictions(data.predictions || []);
        setTrendData(data.trends || []);
        if (isRefresh) toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching command center data:', error);
      // Load mock data for demo
      loadMockData();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Mock data for demonstration
  const loadMockData = () => {
    setKpiData({
      financial: {
        revenue: { value: '$187K', status: 'warning', trend: 'up', trendValue: '+3.2%' },
        net_margin: { value: '10.5%', status: 'warning', trend: 'down', trendValue: '-1.5%' },
        cost_per_episode: { value: '$23.5K', status: 'good', trend: 'stable', trendValue: '0%' },
        adr_rate: { value: '4.2%', status: 'good', trend: 'down', trendValue: '-0.3%' },
        bad_debt: { value: '2.1%', status: 'good', trend: 'stable', trendValue: '0%' }
      },
      clinical: {
        pain_control: { value: '92%', status: 'excellent', trend: 'up', trendValue: '+2.1%' },
        hospitalization: { value: '3.2%', status: 'excellent', trend: 'down', trendValue: '-0.8%' },
        live_discharge: { value: '2.1%', status: 'excellent', trend: 'stable', trendValue: '0%' },
        symptom_management: { value: '95%', status: 'excellent', trend: 'up', trendValue: '+1.0%' },
        missed_visits: { value: '3.8%', status: 'good', trend: 'down', trendValue: '-0.5%' }
      },
      workforce: {
        staff_utilization: { value: '77.2%', status: 'good', trend: 'up', trendValue: '+2.3%' },
        burnout_risk: { value: '3', status: 'warning', trend: 'up', trendValue: '+1' },
        prn_utilization: { value: '9.2%', status: 'good', trend: 'down', trendValue: '-1.1%' },
        turnover_rate: { value: '8%', status: 'excellent', trend: 'stable', trendValue: '0%' },
        hiring_gap: { value: '2', status: 'warning', trend: 'stable', trendValue: '0' }
      },
      compliance: {
        overall_compliance: { value: '96%', status: 'excellent', trend: 'up', trendValue: '+1.0%' },
        audit_readiness: { value: '85%', status: 'good', trend: 'up', trendValue: '+3.0%' },
        cti_compliance: { value: '98%', status: 'warning', trend: 'down', trendValue: '-2.0%' },
        f2f_compliance: { value: '100%', status: 'excellent', trend: 'stable', trendValue: '0%' },
        chap_score: { value: '88%', status: 'good', trend: 'up', trendValue: '+2.0%' }
      },
      census: {
        adc: { value: '50.2', status: 'good', trend: 'up', trendValue: '+3.2%' },
        admissions: { value: '8', status: 'warning', trend: 'down', trendValue: '-2' },
        discharges: { value: '6', status: 'good', trend: 'stable', trendValue: '0' },
        alos: { value: '68', status: 'good', trend: 'up', trendValue: '+3 days' },
        referral_conversion: { value: '72%', status: 'good', trend: 'up', trendValue: '+5%' }
      },
      quality: {
        cahps_overall: { value: '91%', status: 'excellent', trend: 'up', trendValue: '+1.5%' },
        complaint_rate: { value: '2.3', status: 'excellent', trend: 'down', trendValue: '-0.5' },
        resolution_time: { value: '3.2', status: 'excellent', trend: 'down', trendValue: '-0.8 days' },
        family_satisfaction: { value: '88%', status: 'good', trend: 'up', trendValue: '+2%' }
      }
    });

    setAlerts([
      { id: 1, severity: 'critical', title: 'Burnout Risk Alert', domain: 'Workforce', message: '3 staff members showing critical burnout indicators. Immediate intervention recommended.', recommendation: 'Review caseload distribution and schedule 1:1 meetings.', action: '/burnout-dashboard' },
      { id: 2, severity: 'high', title: 'Revenue Below Target', domain: 'Financial', message: 'Weekly revenue at $187K vs $200K target (93.5%). Census growth needed.', recommendation: 'Focus on referral conversion and admission velocity.', action: '/monthly-financials' },
      { id: 3, severity: 'high', title: 'CTI Compliance Decline', domain: 'Compliance', message: 'CTI compliance dropped to 98%. 2 CTIs pending completion.', recommendation: 'Prioritize CTI documentation before billing submission.', action: '/clinical-quality' },
      { id: 4, severity: 'medium', title: 'Hiring Gap', domain: 'Workforce', message: '2 FTE hiring gap based on census growth projections.', recommendation: 'Initiate recruitment for RN/CM positions within 30 days.', action: '/hiring-predictions' },
      { id: 5, severity: 'low', title: 'CHAP Score Improvement', domain: 'Compliance', message: 'CHAP readiness score improved to 88%. Continue current trajectory.', recommendation: 'Focus on remaining documentation gaps.', action: '/audit-ready' }
    ]);

    setInsights([
      { type: 'prediction', title: 'Census Growth Projection', description: 'ADC expected to reach 54 within 4 weeks based on current admission trends.', impact: 'High' },
      { type: 'risk', title: 'Capacity Strain Ahead', description: 'At 54 ADC, staff utilization will exceed 85%. Plan for additional coverage.', impact: 'High' },
      { type: 'recommendation', title: 'Revenue Optimization', description: 'Improving referral conversion by 5% could add $12K weekly revenue.', impact: 'Medium' },
      { type: 'trend', title: 'Clinical Quality Improving', description: 'Pain control and hospitalization metrics show consistent 8-week improvement.', impact: 'Positive' },
      { type: 'opportunity', title: 'PRN Cost Savings', description: 'PRN utilization down 1.1%. Potential $3K monthly savings if sustained.', impact: 'Medium' }
    ]);

    setTrendData([
      { week: 'W1', adc: 48, revenue: 175, quality: 89, compliance: 94 },
      { week: 'W2', adc: 49, revenue: 180, quality: 90, compliance: 95 },
      { week: 'W3', adc: 50, revenue: 182, quality: 91, compliance: 95 },
      { week: 'W4', adc: 50.2, revenue: 187, quality: 92, compliance: 96 }
    ]);

    setPredictions([
      { metric: 'ADC', current: 50.2, forecast: 54, confidence: 85, trend: 'up' },
      { metric: 'Revenue', current: 187, forecast: 195, confidence: 75, trend: 'up' },
      { metric: 'Staff Utilization', current: 77.2, forecast: 82, confidence: 80, trend: 'up' },
      { metric: 'Compliance', current: 96, forecast: 97, confidence: 90, trend: 'stable' }
    ]);
  };

  useEffect(() => {
    fetchCommandCenterData();
    fetchWeekOverWeek();
  }, [token, timeRange]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    let criticalAlerts = alerts.filter(a => a.severity === 'critical').length;
    let kpisOnTarget = 0;
    let kpisWarning = 0;
    let kpisCritical = 0;
    
    Object.values(kpiData).forEach(domain => {
      Object.values(domain).forEach(kpi => {
        if (kpi.status === 'excellent' || kpi.status === 'good') kpisOnTarget++;
        else if (kpi.status === 'warning') kpisWarning++;
        else if (kpi.status === 'critical') kpisCritical++;
      });
    });

    return {
      criticalAlerts,
      kpisOnTarget,
      kpisWarning,
      kpisCritical,
      totalKpis: kpisOnTarget + kpisWarning + kpisCritical,
      healthScore: Math.round((kpisOnTarget / (kpisOnTarget + kpisWarning + kpisCritical || 1)) * 100)
    };
  }, [alerts, kpiData]);

  // Radar chart data
  const radarData = useMemo(() => {
    const domains = ['Financial', 'Clinical', 'Workforce', 'Compliance', 'Census', 'Quality'];
    return domains.map(domain => {
      const domainKey = domain.toLowerCase();
      const domainData = kpiData[domainKey] || {};
      let score = 0;
      let count = 0;
      Object.values(domainData).forEach(kpi => {
        count++;
        if (kpi.status === 'excellent') score += 100;
        else if (kpi.status === 'good') score += 80;
        else if (kpi.status === 'warning') score += 60;
        else score += 40;
      });
      return {
        domain,
        score: count > 0 ? Math.round(score / count) : 0,
        fullMark: 100
      };
    });
  }, [kpiData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="command-center-loading">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="executive-command-center">
      {/* Header */}
      <PageHeader
        icon={LayoutDashboard}
        title="Executive Command Center"
        subtitle="Real-time operational intelligence for COO decision-making"
        gradient="from-slate-800 via-slate-700 to-slate-900"
      >
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[120px] bg-white/20 border-0 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => fetchCommandCenterData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button className="bg-white text-slate-800 hover:bg-white/90">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </PageHeader>

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Org Health Score"
          value={`${summaryMetrics.healthScore}%`}
          icon={Gauge}
          gradient={summaryMetrics.healthScore >= 80 ? 'emerald' : summaryMetrics.healthScore >= 60 ? 'amber' : 'rose'}
          subtitle={`${summaryMetrics.kpisOnTarget} KPIs on target`}
        />
        <MetricCard
          title="Critical Alerts"
          value={summaryMetrics.criticalAlerts}
          icon={AlertTriangle}
          gradient={summaryMetrics.criticalAlerts === 0 ? 'emerald' : summaryMetrics.criticalAlerts <= 2 ? 'amber' : 'rose'}
          subtitle="Requiring immediate attention"
        />
        <MetricCard
          title="KPIs At Risk"
          value={summaryMetrics.kpisWarning + summaryMetrics.kpisCritical}
          icon={Target}
          gradient={summaryMetrics.kpisWarning + summaryMetrics.kpisCritical === 0 ? 'emerald' : 'amber'}
          subtitle={`${summaryMetrics.kpisCritical} critical, ${summaryMetrics.kpisWarning} warning`}
        />
        <MetricCard
          title="AI Predictions"
          value={predictions.length}
          icon={Brain}
          gradient="purple"
          subtitle="4-week forecasts ready"
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="report" className={modernTabTriggerClass}>
            <FileText className="h-4 w-4 mr-2" /> Report
          </TabsTrigger>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="kpis" className={modernTabTriggerClass}>
            <Target className="h-4 w-4 mr-2" /> All KPIs
          </TabsTrigger>
          <TabsTrigger value="alerts" className={modernTabTriggerClass}>
            <AlertTriangle className="h-4 w-4 mr-2" /> Alerts
            {summaryMetrics.criticalAlerts > 0 && (
              <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">{summaryMetrics.criticalAlerts}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="insights" className={modernTabTriggerClass}>
            <Sparkles className="h-4 w-4 mr-2" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="predictions" className={modernTabTriggerClass}>
            <Brain className="h-4 w-4 mr-2" /> Predictions
          </TabsTrigger>
          <TabsTrigger value="history" className={modernTabTriggerClass}>
            <Clock className="h-4 w-4 mr-2" /> Data History
          </TabsTrigger>
        </TabsList>

        {/* Enhanced Report Tab */}
        <TabsContent value="report" className="space-y-6">
          <EnhancedReportSection 
            domain="all"
            title="Executive Command Center Report"
            description="Cross-domain metrics and KPIs with AI-powered insights and recommendations"
            defaultTimeframe="30d"
          />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Week-over-Week Highlights */}
          {weekOverWeek.highlights && weekOverWeek.highlights.length > 0 && (
            <ModernCard>
              <CardHeader className="pb-2">
                <SectionHeader icon={TrendingUp} title="Week-over-Week Changes" subtitle="Key metric movements vs last week" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {weekOverWeek.highlights.map((highlight, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border-l-4 ${
                        highlight.color === 'green' 
                          ? 'border-l-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
                          : highlight.color === 'red'
                          ? 'border-l-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-l-slate-400 bg-slate-50 dark:bg-slate-800/20'
                      }`}
                      data-testid={`wow-highlight-${highlight.metric?.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {highlight.color === 'green' ? (
                          <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                        ) : highlight.color === 'red' ? (
                          <ArrowDownRight className="h-4 w-4 text-red-600" />
                        ) : (
                          <Minus className="h-4 w-4 text-slate-500" />
                        )}
                        <span className={`text-sm font-semibold ${
                          highlight.color === 'green' ? 'text-emerald-700 dark:text-emerald-400' :
                          highlight.color === 'red' ? 'text-red-700 dark:text-red-400' :
                          'text-slate-600 dark:text-slate-400'
                        }`}>
                          {highlight.metric}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{highlight.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </ModernCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Domain Health Radar */}
            <ModernCard className="lg:col-span-1">
              <CardHeader>
                <SectionHeader icon={PieChartIcon} title="Domain Health" subtitle="Performance by area" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Score" dataKey="score" stroke="#6366F1" fill="#6366F1" fillOpacity={0.5} />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </ModernCard>

            {/* Trend Chart */}
            <ModernCard className="lg:col-span-2">
              <CardHeader>
                <SectionHeader icon={TrendingUp} title="Performance Trends" subtitle="Key metrics over time" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="revenue" name="Revenue ($K)" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="quality" name="Quality %" stroke="#6366F1" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="compliance" name="Compliance %" stroke="#F59E0B" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </ModernCard>
          </div>

          {/* Critical KPIs Quick View */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={Flame} title="Critical KPIs" subtitle="High-priority metrics requiring COO attention" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {Object.entries(KPI_DOMAINS).map(([domainKey, domain]) => 
                  domain.kpis.filter(kpi => kpi.critical).map(kpi => {
                    const data = kpiData[domainKey]?.[kpi.id] || {};
                    return (
                      <KPICard 
                        key={`${domainKey}-${kpi.id}`}
                        kpi={kpi}
                        value={data.value || 'N/A'}
                        status={data.status || 'pending'}
                        trend={data.trend}
                        trendValue={data.trendValue}
                      />
                    );
                  })
                )}
              </div>
            </CardContent>
          </ModernCard>

          {/* Alerts Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ModernCard>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <SectionHeader icon={AlertTriangle} title="Priority Alerts" subtitle="Items needing attention" />
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('alerts')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.slice(0, 3).map(alert => (
                    <AlertItem key={alert.id} alert={alert} onAction={() => {}} />
                  ))}
                </div>
              </CardContent>
            </ModernCard>

            <ModernCard>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <SectionHeader icon={Sparkles} title="AI Insights" subtitle="Intelligent recommendations" />
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('insights')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {insights.slice(0, 3).map((insight, idx) => (
                    <InsightCard key={idx} insight={insight} />
                  ))}
                </div>
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* All KPIs Tab */}
        <TabsContent value="kpis" className="space-y-6">
          <div className="flex items-center gap-4 mb-4">
            <Select value={selectedDomain} onValueChange={setSelectedDomain}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                {Object.entries(KPI_DOMAINS).map(([key, domain]) => (
                  <SelectItem key={key} value={key}>{domain.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {Object.entries(KPI_DOMAINS)
            .filter(([key]) => selectedDomain === 'all' || selectedDomain === key)
            .map(([domainKey, domain]) => {
              const Icon = domain.icon;
              return (
                <ModernCard key={domainKey}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-xl bg-gradient-to-br ${domain.gradient}`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle>{domain.name}</CardTitle>
                        <CardDescription>{domain.kpis.length} KPIs tracked</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {domain.kpis.map(kpi => {
                        const data = kpiData[domainKey]?.[kpi.id] || {};
                        return (
                          <KPICard 
                            key={kpi.id}
                            kpi={kpi}
                            value={data.value || 'N/A'}
                            status={data.status || 'pending'}
                            trend={data.trend}
                            trendValue={data.trendValue}
                          />
                        );
                      })}
                    </div>
                  </CardContent>
                </ModernCard>
              );
            })}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={AlertTriangle} title="All Alerts & Notifications" subtitle="Prioritized by severity" />
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-4 pr-4">
                  {alerts.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                      <h3 className="font-semibold text-emerald-600">All Clear</h3>
                      <p className="text-muted-foreground mt-2">No active alerts at this time</p>
                    </div>
                  ) : (
                    alerts.map(alert => (
                      <AlertItem key={alert.id} alert={alert} onAction={() => {}} />
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights">
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader icon={Sparkles} title="AI-Powered Insights" subtitle="Intelligent analysis and recommendations" />
                <InsightsExport 
                  insights={insights.map(i => ({
                    title: i.title || i.message?.substring(0, 50),
                    description: i.message || i.description,
                    severity: i.severity || i.priority || 'info',
                    category: i.domain || 'General',
                    recommendation: i.action || i.recommendation
                  }))} 
                  pageTitle="Command Center" 
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-6">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={Brain} title="4-Week Forecasts" subtitle="AI-powered predictions based on current trends" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {predictions.map((pred, idx) => (
                  <Card key={idx} className="text-center">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground mb-2">{pred.metric}</p>
                      <div className="flex items-center justify-center gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Current</p>
                          <p className="text-2xl font-bold">{pred.current}</p>
                        </div>
                        <TrendingUp className={`h-6 w-6 ${pred.trend === 'up' ? 'text-emerald-500' : 'text-slate-400'}`} />
                        <div>
                          <p className="text-xs text-muted-foreground">Forecast</p>
                          <p className="text-2xl font-bold text-[#00b0ab]">{pred.forecast}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {pred.confidence}% confidence
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </ModernCard>

          <ModernCard>
            <CardHeader>
              <SectionHeader icon={TrendingUp} title="Forecast Visualization" />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={[
                  { week: 'Current', adc: 50.2, revenue: 187 },
                  { week: 'Week 1', adc: 51, revenue: 189 },
                  { week: 'Week 2', adc: 52, revenue: 191 },
                  { week: 'Week 3', adc: 53, revenue: 193 },
                  { week: 'Week 4', adc: 54, revenue: 195 }
                ]}>
                  <defs>
                    <linearGradient id="colorAdc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="adc" name="Projected ADC" stroke="#6366F1" fill="url(#colorAdc)" />
                  <Area yAxisId="right" type="monotone" dataKey="revenue" name="Projected Revenue ($K)" stroke="#10B981" fill="url(#colorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-6">
          <ModernCard>
            <CardHeader>
              <SectionHeader 
                icon={Clock} 
                title="Data History" 
                subtitle="View timeline of data entries and audit logs for all KPI categories" 
              />
            </CardHeader>
            <CardContent className="space-y-4">
              <DataHistoryPanel 
                category="census" 
                title="Census & Operations" 
                isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              />
              <DataHistoryPanel 
                category="clinical" 
                title="Clinical Quality" 
                isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              />
              <DataHistoryPanel 
                category="financial" 
                title="Financial Data" 
                isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              />
              <DataHistoryPanel 
                category="compliance" 
                title="Compliance" 
                isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              />
              <DataHistoryPanel 
                category="workforce" 
                title="Workforce Metrics" 
                isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
              />
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
